/**
 * One measure from the patient's Prism phone scans over time, with the check-ins drawn as dashed lines, so the trend
 * reads next to them (Reference ID sheet v12 "Read me" item 5). Only what Prism returned is drawn: a scan that did not
 * return the measure has no point, and nothing is filled in for it (DEV-BG-03); the line joins measured points. The
 * baseline (the first COMPLETE scan, DEV-BG-05) is a ringed point; a PARTIAL scan's value is a hollow point.
 *
 * Check-in labels never print over each other: two check-ins a day apart read "M4 | M5", one label on each side of
 * its line (checkinLabelPlacement), at every width.
 */

import { useEffect, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate } from '../lib/format';

export interface ScanPoint {
  scanId: string;
  takenAt: string;
  month: number | null;
  value: number;
  partial: boolean;
  baseline: boolean;
}

interface Row extends ScanPoint {
  t: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const tick = (t: number) => {
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** One decimal, as Prism's values are stored (lib/scan/prism.ts rounds to 0.1), so a column lines up. */
export function formatMeasure(value: number, unit: string): string {
  const n = value.toFixed(1);
  return unit === '%' ? `${n}%` : `${n} ${unit}`;
}

/** Three or four evenly spaced whole-number ticks around the values, so the axis reads 32, 34, 36, 38. */
function axisTicks(values: number[]): number[] {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const raw = Math.max((hi - lo) / 3, 0.5);
  const step = [1, 2, 5, 10, 20, 50].find((s) => s >= raw) ?? 100;
  const start = Math.floor((lo - step * 0.5) / step) * step;
  const end = Math.ceil((hi + step * 0.5) / step) * step;
  const ticks: number[] = [];
  for (let t = start; t <= end + 1e-9; t += step) ticks.push(Math.round(t * 10) / 10);
  return ticks;
}

export type LabelPlacement = 'center' | 'left' | 'right' | 'hidden';

/**
 * Where each check-in label goes, from the lines' x positions in pixels (oldest first). Lines closer than `minGap`
 * form a group: a lone line keeps its label centred above it; in a group the first label sits left of its line and the
 * last right of its line, and any in between are left out (their dashed lines still show). `minGap` is about twice
 * the widest label, so neighbouring groups never touch either.
 */
export function checkinLabelPlacement(xs: number[], minGap = 50): LabelPlacement[] {
  const out: LabelPlacement[] = xs.map(() => 'center');
  let start = 0;
  for (let i = 1; i <= xs.length; i++) {
    if (i < xs.length && xs[i]! - xs[i - 1]! < minGap) continue;
    const size = i - start;
    if (size > 1) {
      out[start] = 'left';
      out[i - 1] = 'right';
      for (let j = start + 1; j < i - 1; j++) out[j] = 'hidden';
    }
    start = i;
  }
  return out;
}

const MARGIN = { top: 18, right: 14, bottom: 0, left: -10 };
const Y_AXIS_WIDTH = 44;

/** The chart's own width in pixels, so the label layout follows the screen (390 px phone or 1440 px desktop). */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

function CheckinLabel({ viewBox, text, placement }: { viewBox?: { x?: number; y?: number }; text: string; placement: LabelPlacement }) {
  if (placement === 'hidden' || viewBox?.x === undefined || viewBox.y === undefined) return null;
  const dx = placement === 'left' ? -3 : placement === 'right' ? 3 : 0;
  const anchor = placement === 'left' ? 'end' : placement === 'right' ? 'start' : 'middle';
  return (
    <text x={viewBox.x + dx} y={viewBox.y - 6} textAnchor={anchor} fontSize={10.5} fill="var(--ink-muted)">
      {text}
    </text>
  );
}

function Dot(props: { cx?: number; cy?: number; payload?: Row }) {
  const { cx, cy, payload } = props;
  if (cx === undefined || cy === undefined || !payload) return null;
  if (payload.baseline) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={7.5} fill="var(--glass-standard-bg)" stroke="var(--forest)" strokeWidth={2} />
        <circle cx={cx} cy={cy} r={3.2} fill="var(--forest)" />
      </g>
    );
  }
  if (payload.partial) return <circle cx={cx} cy={cy} r={4.5} fill="var(--glass-standard-bg)" stroke="var(--sage)" strokeWidth={2} />;
  return <circle cx={cx} cy={cy} r={4.5} fill="var(--sage)" stroke="var(--glass-standard-bg)" strokeWidth={1.5} />;
}

function TipBox({ active, payload, unit, label }: { active?: boolean; payload?: Array<{ payload: Row }>; unit: string; label: string }) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="rounded-sm2 border border-hairline bg-white px-3 py-2 text-[12px] shadow-pop">
      <p className="font-semibold text-navy">
        {label} {formatMeasure(row.value, unit)}
      </p>
      <p className="mt-0.5 text-ink-muted">
        {formatDate(row.takenAt)}
        {row.month !== null ? `, month ${row.month}` : ', intake'}
      </p>
      {(row.baseline || row.partial) && <p className="mt-0.5 text-ink-muted">{row.baseline ? 'Baseline' : 'Partial scan'}</p>}
    </div>
  );
}

export function BodyScanChart({
  points,
  checkins,
  label,
  unit,
}: {
  points: ScanPoint[];
  checkins: Array<{ month: number; at: string }>;
  label: string;
  unit: string;
}) {
  const rows: Row[] = points.map((p) => ({ ...p, t: new Date(p.takenAt).getTime() }));
  const times = [...rows.map((r) => r.t), ...checkins.map((c) => new Date(c.at).getTime())];
  const min = Math.min(...times);
  const max = Math.max(...times);
  const pad = Math.max((max - min) * 0.06, 3 * 86_400_000);
  const ticks = axisTicks(rows.map((r) => r.value));
  const { ref, width } = useWidth();

  // Label layout from where each check-in line falls on this chart's x axis.
  const lo = min - pad;
  const hi = max + pad;
  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right - Y_AXIS_WIDTH);
  const sorted = [...checkins].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const placements = plotWidth > 0 && hi > lo
    ? checkinLabelPlacement(sorted.map((c) => ((new Date(c.at).getTime() - lo) / (hi - lo)) * plotWidth))
    : sorted.map((): LabelPlacement => 'center');

  return (
    <div ref={ref} className="h-56 w-full" data-testid="body-scan-chart" role="img" aria-label={`${label} from each phone scan, with the check-ins marked`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={MARGIN}>
          <CartesianGrid vertical={false} stroke="var(--hairline)" strokeOpacity={0.6} />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={[lo, hi]}
            tickFormatter={tick}
            tick={{ fontSize: 11, fill: 'var(--ink-muted)' }}
            stroke="var(--hairline)"
            minTickGap={24}
          />
          <YAxis
            domain={[ticks[0]!, ticks[ticks.length - 1]!]}
            ticks={ticks}
            interval={0}
            tick={{ fontSize: 11, fill: 'var(--ink-muted)' }}
            stroke="var(--hairline)"
            width={Y_AXIS_WIDTH}
            tickFormatter={(v: number) => String(v)}
          />
          {sorted.map((c, i) => (
            <ReferenceLine
              key={c.month}
              x={new Date(c.at).getTime()}
              stroke="var(--ink-muted)"
              strokeOpacity={0.45}
              strokeDasharray="3 4"
              label={<CheckinLabel text={`M${c.month}`} placement={placements[i] ?? 'center'} />}
            />
          ))}
          <Tooltip content={<TipBox unit={unit} label={label} />} cursor={{ stroke: 'var(--hairline)' }} />
          <Line type="linear" dataKey="value" stroke="var(--sage)" strokeWidth={2} isAnimationActive={false} dot={<Dot />} activeDot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
